import React, { useEffect, useState } from 'react'
import ToastMessage from '../../../../common/ToastMessage';
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useContractors from '../../../../store/ReactQueries/contractors/useContractor';
import useAddConcractors from '../../../../store/ReactQueries/contractors/useAddContractors';
import useDeleteContractor from '../../../../store/ReactQueries/contractors/useDeleteContractor';
import useUpdateContractors from '../../../../store/ReactQueries/contractors/useUpdateContractors';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../utils/constants';
import { FieldErrors } from '../../../../domain/helpers/Errors';
import { formatDate } from '../../../../common/GlobalFunctions';
import { validateSchema } from '../../../../common/ValidateSchema';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { ContractorInterface } from '../../../../models/TicketModule/Contractor';
import { contractorSchema } from '../../../../domain/ValidationSchemas/Ticket/contractor';

export const Contractors = () => {

    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<ContractorInterface>({
        id: '',
        title: '',
        isDefault: false,
    });

    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });

    let { data: contractors, error: contractorError, isLoading, refetch } = useContractors(page, pageSize);

    const addContractor = useAddConcractors(page, pageSize);
    const deleteContractor = useDeleteContractor(page, pageSize);
    const updateContractor = useUpdateContractors(page, pageSize);

    useEffect(() => {
        if (contractorError) ToastMessage.Failed(`Error: ${contractorError.message}`);
    }, [contractorError])

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'title', headerName: 'Title', type: "string", editable: true, width: 200 },
        {
            field: 'isDefault',
            headerName: 'IsDefault',
            type: 'boolean',
            editable: true,
            renderCell: ({ value }) => (
                <span style={{ color: darkMode ? 'white' : 'black' }}>
                    {value ? `✓` : '✕'}
                </span>
            ),
        },
        { field: 'createdDate', headerName: 'CreatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'updatedDate', headerName: 'UpdatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'tenantId', headerName: 'TenantId', type: "string", editable: false, width: 150 },
        { field: 'authorId', headerName: 'AuthorId', type: "string", editable: false, width: 150 },
        { field: 'modifiedById', headerName: 'ModifiedById', type: "string", editable: false, width: 150 },
    ];

    const addItems = () => {
        setForm({ ...form, title: '', id: '', isDefault: false });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleSave = async () => {
        const isValid = validateSchema(contractorSchema, { title: form.title });

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const upForm: ContractorInterface = {
            id: form.id,
            title: form.title,
            isDefault: form.isDefault
        };

        if (upForm.id === '') {
            const { id, ...formData } = upForm;
            addContractor.mutate(formData, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        } else {
            updateContractor.mutate(upForm, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        }
        setModal({ ...modal, add: false, edit: false, view: false });
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) {
            console.log(error.message);
            ToastMessage.Failed(error.message);
        } else {
            console.log("An unknown error occurred");
            ToastMessage.Failed();
        }
    };

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deleteContractor.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language['Contractors']}
            rows={contractors || []}
            showColumnModal={['title', 'isDefault']}
            hideColumns={['authorId', 'id', 'tenantId', 'modifiedById']}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            loading={isLoading}
            pageSize={pageSize}
            setPage={setPage}
            setForm={setForm}
            addItems={addItems}
            setModal={setModal}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
        />
    );
}
export default Contractors;